/**
 * src/lib/voice/useUploadJobTracker.js — TICKET-UPLOAD-PROGRESS-UX (CEO 2026-06-07).
 *
 * Tab-INDEPENDENT phase advance for the History "Processing recording" stepper.
 *
 * The stepper toast is mounted globally (AppShell), but the FIRST reconcile pass
 * lived inside HistoryTab — so a job's phase only advanced while that tab was
 * mounted. The CEO's whole "you can keep working" intent needs the phase to keep
 * moving after you navigate away. This hook closes that gap: while any job is
 * non-terminal, it polls voice_uploads for the job's project (the same
 * fetchVoiceUploads HistoryTab uses) and reconciles each job's phase from the
 * row's status (matched by idempotency_key = recording_id). It stops the moment
 * every job is terminal (ready/error), so there's no idle polling.
 *
 * It's called from ProcessingRecordingToast — a small, already-store-subscribed
 * leaf — so its re-renders don't cascade through AppShell. HistoryTab keeps its
 * own realtime-driven reconcile for instant updates while you're on the tab;
 * this is the slower, always-on safety net. Both patches are idempotent.
 */
import { useEffect } from 'react';
import { useSyncExternalStore } from 'react';
import {
  getUploadJobs,
  subscribeUploadJobs,
  patchUploadJob,
  uploadStatusToPhase,
} from './uploadStatusStore';
import { fetchVoiceUploads } from '../api/voice-v2';

const TERMINAL = new Set(['ready', 'error']);

function activeProjectKey(jobs) {
  const ids = new Set();
  for (const j of jobs) {
    if (j && j.projectId && !TERMINAL.has(j.phase)) ids.add(j.projectId);
  }
  return [...ids].sort().join(',');
}

export function useUploadJobTracker({ pollMs = 5000, fetchUploadsImpl = fetchVoiceUploads } = {}) {
  const jobs = useSyncExternalStore(subscribeUploadJobs, getUploadJobs);
  // Stable primitive dep: re-arm the poller only when the SET of active project
  // ids changes (not on every pct tick), so progress updates don't churn timers.
  const projectKey = activeProjectKey(jobs);

  useEffect(() => {
    if (!projectKey) return undefined;
    let cancelled = false;

    const tick = async () => {
      // Re-read live each tick — jobs may have been added/dismissed since arm.
      const active = getUploadJobs().filter((j) => j.projectId && !TERMINAL.has(j.phase));
      const byProject = new Map();
      for (const j of active) {
        const arr = byProject.get(j.projectId) || [];
        arr.push(j);
        byProject.set(j.projectId, arr);
      }
      for (const [projectId, projectJobs] of byProject) {
        let rows;
        try {
          rows = await fetchUploadsImpl(projectId);
        } catch {
          continue; // spine-emit-ok: best-effort poll; next tick retries
        }
        if (cancelled || !Array.isArray(rows)) continue;
        for (const job of projectJobs) {
          const row = rows.find((r) => r.idempotency_key && r.idempotency_key === job.id);
          if (!row) continue;
          const phase = uploadStatusToPhase(row.status);
          if (phase && phase !== job.phase) {
            patchUploadJob(job.id, { phase, uploadId: row.id });
          }
        }
      }
    };

    tick(); // immediate pass so a returning user doesn't wait a full interval
    const id = setInterval(tick, pollMs);
    return () => { cancelled = true; clearInterval(id); };
  }, [projectKey, pollMs, fetchUploadsImpl]);
}
